import React, { useEffect, useState } from 'react';

import config from 'config';
import styled from 'styled-components';

import HeroBanner from '~/layout/components/HeroBanner';
import {
  desktop,
  CuralateProduct,
  useBreakpoint,
  Modal,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import CompressionGuideOverlay from '../CompressionGuideOverlay';
import { compressions } from '../CompressionGuideOverlay/constants';
import CompressionScale from '../CompressionScale';
import FabricCardsLayout from '../FabricCardsLayout';

const StyledCompressionScale = styled(CompressionScale)`
  max-width: 327px;
  margin: 0 auto;
`;

const Wrapper = styled.div`
  padding-bottom: 148px;
`;

const CompressionHeader = styled.h4`
  font-weight: bold;
  font-size: 20px;
  text-align: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.compressionGuideSectionBg};
  padding: 32px;
`;

const InfoSection = styled(Section)`
  background: ${({ theme }) => theme.colors.palette.white};
`;

const CardSection = styled(Section)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CompressionInfo = styled.p`
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  line-height: ${({ theme }) => theme.sizes.sm}px;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.colors.textDefault};
  margin-top: 8px;
  margin-bottom: ${({ theme }) => theme.space('s')};

  ${desktop`
    margin-bottom: 20px
  `}
`;

const Choose = styled.p`
  font-weight: 400;
  margin-top: ${({ theme }) => theme.sizes.xxs}px;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textDefault};
`;

const Break = styled.hr`
  border: 1px solid ${({ theme }) => theme.colors.compressionGuideBreakBorder};
  margin: ${({ theme }) => theme.sizes.sm}px auto;
  width: 50px;
`;

const curalateModalContainerId = config.get(
  'public.curalate.curalateModalContainerId'
);

const CompressionGuideLayout = () => {
  const { low, medium } = compressions;
  const { isMobile } = useBreakpoint();
  const [overlayContent, setOverlayContent] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleLearnMoreClick = (content) => {
    setOverlayContent(content);
    setIsOpen(true);
  };

  const modalAnimation = isMobile
    ? {
        transitionFrom: { transform: 'translateY(100vh)' },
        transitionEnter: { transform: 'translateY(0)' },
        transitionLeave: { transform: 'translateY(100vh)' },
        springConfig: {
          tension: 500,
          friction: 95,
        },
      }
    : {};

  const [bazaarHasFocus, setBazaarHasFocus] = useState(false);

  useEffect(() => {
    // Since Bazaar opens up a modal adjacent to our modal (and not nested
    // within), the FocusTrap on the original modal blocks clicks on the Bazaar
    // modal. Unfortunately, there is no documented API for the Bazaar UGC
    // script to indicate when it has opened a modal. We could poll for the
    // Bazaar modal state using some observations about how it behaves when
    // opening a modal, but we would miss out on the initial focus event that
    // puts focus inside their modal. So we attach an event listener for the
    // initial focus() call that presumably occurs inside the Bazaar code when
    // the modals are initially opened.
    let isFocusOutHandlerSet = false;
    const focusInHandler = (focusEvent) => {
      if (!focusEvent.target) {
        return;
      }

      // Only continue if the focus event happened inside one of the modals
      // created by the UGC script
      const bazaarUGCParent = focusEvent.target.closest('[id^=crl8-]');
      if (!bazaarUGCParent || !bazaarUGCParent.id.match(/-(modal|uploader)$/)) {
        return;
      }

      if (!isFocusOutHandlerSet) {
        // Attach a focusout handler to reset the state when we exit a modal
        const focusOutHandler = (focusOutEvent) => {
          if (!bazaarUGCParent.contains(focusOutEvent.relatedTarget)) {
            setBazaarHasFocus(false);
            bazaarUGCParent.removeEventListener('focusout', focusOutHandler);
            isFocusOutHandlerSet = false;
          }
        };
        bazaarUGCParent.addEventListener('focusout', focusOutHandler);
        isFocusOutHandlerSet = true;
      }

      setBazaarHasFocus(true);
    };

    // The listener is added with the useCapture argument set to true so we can
    // preempt the FocusTrap event handler, which would block the focus event
    // otherwise.
    window.addEventListener('focusin', focusInHandler, true);

    return () => {
      window.removeEventListener('focusin', focusInHandler, true);
    };
  }, []);

  const focusTrapConfig = { active: !bazaarHasFocus };

  return (
    <Wrapper>
      <HeroBanner
        assetName={
          isMobile
            ? 'Compression_Guide_hero_vid_m'
            : 'Compression_Guide_hero_vid_d'
        }
        heroHeaderComponent={
          <FormattedMessage
            defaultMessage="HUGGED. SNUGGED. SUPPORTED."
            id="compression_guide.hero_header"
          />
        }
        heroSubheaderComponent={
          <FormattedMessage
            defaultMessage="You decide how you want to feel."
            id="compression_guide.hero_sub_header"
          />
        }
      />
      <InfoSection>
        <Choose>
          <FormattedMessage
            defaultMessage="Choose your compression"
            id="compression_guide.sub_header"
          />
        </Choose>
        <Break />
        <CompressionHeader>
          <FormattedMessage
            defaultMessage="Low Compression"
            id="compression_guide.light_level_header"
          />
        </CompressionHeader>
        <CompressionInfo>
          <FormattedMessage
            defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Turpis tortor enim, at non risus nisi, cursus ac."
            id="compression_guide.light_info"
          />
        </CompressionInfo>
        <StyledCompressionScale compression="low" />
      </InfoSection>
      <CardSection>
        <FabricCardsLayout
          fabrics={low}
          handleLearnMoreClick={handleLearnMoreClick}
        />
      </CardSection>
      <InfoSection>
        <CompressionHeader>
          <FormattedMessage
            defaultMessage="Medium Compression"
            id="compression_guide.medium_level_header"
          />
        </CompressionHeader>
        <CompressionInfo>
          <FormattedMessage
            defaultMessage="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Turpis tortor enim, at non risus nisi, cursus ac."
            id="compression_guide.medium_info"
          />
        </CompressionInfo>
        <StyledCompressionScale compression="medium" />
      </InfoSection>
      <CardSection>
        <FabricCardsLayout
          fabrics={medium}
          handleLearnMoreClick={handleLearnMoreClick}
        />
      </CardSection>

      <Modal
        dialogStyle={{ padding: 0, width: '100%' }}
        focusTrapConfig={focusTrapConfig}
        isOpen={isOpen}
        titleId="compression-guide-overlay"
        onExit={() => setIsOpen(false)}
        {...modalAnimation}
      >
        <CompressionGuideOverlay
          close={() => setIsOpen(false)}
          content={overlayContent}
        >
          <CuralateProduct experienceName={curalateModalContainerId} />
        </CompressionGuideOverlay>
      </Modal>
    </Wrapper>
  );
};

export default CompressionGuideLayout;
